<template>
  <AuthLayout>
    <!-- Сценарий 1: Пользователь авторизован -->
    <div v-if="isAuthenticated" class="card">
      <div class="card-body">
        <div class="app-brand justify-content-center mb-6">
        </div>
        <h4 class="mb-1">Подключение Ozon</h4>
        
        <!-- Обработка callback (когда есть код OAuth) -->
        <div v-if="isProcessingCallback" class="mb-6">
          <div class="processing-message">
            <transition name="fade" mode="out-in">
              <p :key="currentMessageIndex" class="fade-message">{{ currentMessage }}</p>
            </transition>
          </div>
        </div>
        
        <!-- Вариант 1: Сообщение по умолчанию -->
        <p v-else-if="!shouldAutoRedirect" class="mb-6">
          Видим, что у вас уже есть аккаунт.
          Выберите, какое действие хотите совершить:
        </p>
        
        <!-- Вариант 2: Сообщение о перенаправлении -->
        <div v-else class="mb-6">
          <p class="mb-4">
            Вы будете перенаправлены на Озон для подтверждения прав 
            <span v-if="countdown > 0">({{ countdown }} сек.)</span>
          </p>
          <button 
            v-if="countdown > 0"
            class="btn btn-primary d-grid w-100" 
            type="button"
            @click="redirectToOzon(0)">
            Перейти сейчас ({{ countdown }} сек.)
          </button>
        </div>
        
        <p v-if="successMessage" class="success">{{ successMessage }}</p>
        <p v-if="error" class="error">{{ error }}</p>

        <div v-if="!shouldAutoRedirect && !isProcessingCallback" class="mb-4 d-flex flex-column gap-3">
          <button 
            class="btn btn-primary d-grid w-100" 
            type="button"
            @click="openService">
            Открыть сервис
          </button>
          <button 
            class="btn btn-outline-secondary d-grid w-100" 
            type="button"
            @click="redirectToOzon">
            Добавить новый магазин в текущий аккаунт
          </button>
        </div>
      </div>
    </div>

    <!-- Сценарий 2: Пользователь не авторизован -->
    <div v-else class="card">
      <div class="card-body">
        <div class="app-brand justify-content-center mb-6">
        </div>
        <h4 v-if="!isProcessingCallback" class="mb-1">Добро пожаловать в сервис Киндата Профит! 👋</h4>
        
        <!-- Обработка callback (когда есть код OAuth) -->
        <div v-if="isProcessingCallback" class="mb-6">
          <h4 class="mb-1">Подключение Ozon ...</h4>
          <div class="processing-message">
            <transition name="fade" mode="out-in">
              <p :key="currentMessageIndex" class="fade-message">{{ currentMessage }}</p>
            </transition>
          </div>
        </div>
        
        <!-- Обычное сообщение о перенаправлении -->
        <template v-else>
          <p class="mb-2">
            Вы сейчас будете перенаправлены на страницу авторизации Озона 
            <span v-if="countdown > 0">({{ countdown }} сек.)</span>
            Пожалуйста, подтвердите там права на доступы для подключения вашего магазина.
          </p>
          <p class="mb-4">
            Если вы запускаете приложение впервые, мы создадим для вас аккаунт и автоматически подключим ваш первый магазин. 
            После этого вы сможете использовать наш сервис для автоматического ценообразования на маркетплейсах — 
            оптимизируйте цены, увеличивайте прибыль и экономьте время! 🚀
          </p>
          <button 
            v-if="countdown > 0"
            class="btn btn-primary d-grid w-100 mb-6" 
            type="button"
            @click="redirectUnauthenticatedUserImmediately">
            Перейти сейчас ({{ countdown }} сек.)
          </button>
        </template>
        
        <p v-if="error" class="error">{{ error }}</p>
      </div>
    </div>
  </AuthLayout>
</template>

<script>
import { auth } from "@/data/auth";
import AuthLayout from "@/layouts/Auth.vue";
import { getOAuthAuthorizationUrl, processOAuthCallback, processOAuthStoreCallback } from "@/api/user";
import router from "@/router";
import { user } from "@/data/user";

export default {
  name: 'OzonOauthView',
  components: {AuthLayout},
  data() {
    return {
      error: '',
      successMessage: '',
      isAuthenticated: false,
      // Параметры OAuth из URL
      oauthCode: null,
      oauthState: null,
      // Флаг автоматического перенаправления (из параметра URL)
      shouldAutoRedirect: false,
      // Счетчик обратного отсчета (в секундах)
      countdown: 0,
      // Интервал для счетчика
      countdownInterval: null,
      // Сохраненный OAuth URL для немедленного перехода
      savedOAuthUrl: null,
      // Флаг обработки callback
      isProcessingCallback: false,
      // Текущее сообщение для отображения
      currentMessage: '',
      // Индекс текущего сообщения
      currentMessageIndex: 0,
      // Интервал для переключения сообщений
      messageInterval: null,
      // Сообщения для авторизованных пользователей
      authenticatedMessages: [
        'Мы добавляем магазин...',
        'Настраиваем подключение...',
        'Пожалуйста, подождите немного...',
        'Подключаем магию к коду...',
        'Запрашиваем разрешение у Ozon...',
        'Синхронизируем с параллельной вселенной...',
        'Проверяем, не сломалось ли что-то...',
        'Загружаем терпение...',
        'Ищем свободный порт...',
        'Перезагружаем оптимизм...',
        'Спрашиваем у API, как дела...',
        'Ждем ответа от сервера... (он думает)'
      ],
      // Сообщения для неавторизованных пользователей
      unauthenticatedMessages: [
        'Проверяем, был ли у вас аккаунт...',
        'Если аккаунт найден, скоро откроется сервис...',
        'Если аккаунта нет, мы создадим его для вас...',
        'Ищем разработчика, кто написал этот код...',
        'Завариваем кофе для команды...',
        'Считаем до бесконечности... и обратно...',
        'Загружаем терпение...',
        'Ищем иголку в стоге сена...',
        'Проверяем, работает ли интернет...',
        'Спрашиваем у кота совета...',
        'Перезагружаем вселенную...',
        'Перезагружаем оптимизм...',
        'Спрашиваем у API, как дела...',
      ]
    }
  },
  methods: {
    /**
     * Получает часовой пояс пользователя
     * @returns {string} Часовой пояс (например, 'Europe/Moscow')
     */
    getTimezone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    /**
     * Проверяет авторизацию пользователя
     * Проверяет напрямую наличие токена, без вызова методов, которые могут вызвать logout
     */
    checkAuthentication() {
      // Проверяем наличие токена напрямую, чтобы избежать вызова logout
      const token = auth.access_token || localStorage.getItem('access_token');
      this.isAuthenticated = !!token;
    },
    /**
     * Извлекает параметры OAuth из URL query
     */
    extractOAuthParams() {
      const query = this.$route.query;
      this.oauthCode = query.code || null;
      this.oauthState = query.state || null;
      // Проверяем параметр для автоматического перенаправления
      // Если есть параметр redirect=true или auto_redirect=true, то включаем автоматическое перенаправление
      this.shouldAutoRedirect = query.redirect === 'true' || query.auto_redirect === 'true' || query.redirect === '1' || query.auto_redirect === '1';
    },
    /**
     * Получает URL для перенаправления на Ozon OAuth
     * @returns {Promise<string|null>} URL для перенаправления или null в случае ошибки
     */
    async getOzonOAuthUrl() {
      try {
        const response = await getOAuthAuthorizationUrl('ozon');
        const { authorization_url, state } = response.data;
        
        // Сохраняем state для проверки при callback
        // Используем localStorage с timestamp для надежности при переходе на другой домен
        if (state) {
          const stateData = {
            state: state,
            timestamp: Date.now(),
            // State действителен 10 минут
            expiresIn: 10 * 60 * 1000
          };
          localStorage.setItem('oauth_state_ozon', JSON.stringify(stateData));
        }
        
        return authorization_url;
      } catch (error) {
        console.error('Ошибка при получении OAuth URL:', error);
        return null;
      }
    },
    /**
     * Перенаправляет на страницу авторизации Ozon
     * Сценарий 1: Пользователь авторизован
     */
    async redirectToOzon(delay = 0) {
      try {
        const oauthUrl = await this.getOzonOAuthUrl();
        if (oauthUrl) {
          // Сохраняем URL для немедленного перехода
          this.savedOAuthUrl = oauthUrl;
          
          if (delay > 0) {
            // Запускаем счетчик обратного отсчета
            this.countdown = Math.floor(delay / 1000);
            
            this.countdownInterval = setInterval(() => {
              this.countdown--;
              
              if (this.countdown <= 0) {
                clearInterval(this.countdownInterval);
                window.location.href = oauthUrl;
              }
            }, 1000);
          } else {
            window.location.href = oauthUrl;
          }
        } else {
          this.error = 'Не удалось получить URL для авторизации Ozon';
        }
      } catch (error) {
        this.error = error.response?.data?.detail || 'Ошибка при получении URL авторизации Ozon';
      }
    },
    /**
     * Открывает главную страницу сервиса
     * Сценарий 1: Пользователь авторизован
     */
    openService() {
      this.$router.push({name: 'home'});
    },
    /**
     * Проверяет state из callback с сохраненным state
     * @returns {boolean} true если state совпадает и не истек
     */
    validateOAuthState() {
      const savedData = localStorage.getItem('oauth_state_ozon');
      if (!savedData) {
        return false;
      }
      
      try {
        const stateData = JSON.parse(savedData);
        
        // Проверяем, не истек ли state
        const now = Date.now();
        if (now - stateData.timestamp > stateData.expiresIn) {
          // State истек, удаляем его
          localStorage.removeItem('oauth_state_ozon');
          return false;
        }
        
        // Проверяем совпадение state
        const isValid = stateData.state === this.oauthState;
        
        // Удаляем state после проверки (одноразовое использование)
        localStorage.removeItem('oauth_state_ozon');
        
        return isValid;
      } catch (error) {
        // Если не удалось распарсить, удаляем и возвращаем false
        localStorage.removeItem('oauth_state_ozon');
        return false;
      }
    },
    /**
     * Запускает переключение сообщений
     */
    startMessageRotation() {
      const messages = this.isAuthenticated 
        ? this.authenticatedMessages 
        : this.unauthenticatedMessages;
      
      // Устанавливаем первое сообщение
      this.currentMessageIndex = 0;
      this.currentMessage = messages[0];
      
      // Переключаем сообщения каждые 2 секунды
      this.messageInterval = setInterval(() => {
        this.currentMessageIndex = (this.currentMessageIndex + 1) % messages.length;
        this.currentMessage = messages[this.currentMessageIndex];
      }, 2000);
    },
    /**
     * Останавливает переключение сообщений
     */
    stopMessageRotation() {
      if (this.messageInterval) {
        clearInterval(this.messageInterval);
        this.messageInterval = null;
      }
    },
    /**
     * Обрабатывает OAuth callback (если есть параметры в URL)
     */
    async handleOAuthCallback() {
      if (!this.oauthCode) {
        return;
      }

      this.error = '';
      this.isProcessingCallback = true;
      
      // Запускаем переключение сообщений
      this.startMessageRotation();
      
      // Проверяем state для безопасности
      if (this.oauthState && !this.validateOAuthState()) {
        this.error = 'Ошибка безопасности: неверный state параметр';
        this.stopMessageRotation();
        return;
      }
      
      try {
        if (this.isAuthenticated) {
          // Для авторизованных пользователей создаем магазин
          const company_id = await user.getActiveCompany();
          const response = await processOAuthStoreCallback('ozon', this.oauthCode, this.oauthState, company_id);
          
          // Останавливаем переключение сообщений
          this.stopMessageRotation();
          
          if (response.data.store_id) {
            this.successMessage = `Магазин Ozon успешно добавлен! ID магазина: ${response.data.store_id}`;
            
            // Перенаправляем на страницу магазинов через 2 секунды
            setTimeout(async () => {
              await router.push({name: 'stores'});
            }, 2000);
          } else {
            // Если store_id нет, значит произошла ошибка
            // Показываем ошибку от сервера, если она есть
            const errorMessage = response.data?.detail || response.data?.message || response.data?.error || 'Ошибка: магазин не был создан. Попробуйте еще раз.';
            this.error = errorMessage;
          }
        } else {
          // Для неавторизованных пользователей используем API
          const timezone = this.getTimezone();
          const response = await processOAuthCallback('ozon', this.oauthCode, this.oauthState, timezone);
          
          // Сохраняем токены
          if (response.data.access_token && response.data.refresh_token) {
            auth.setToken(response.data.access_token, response.data.refresh_token);
            
            // Обновляем статус авторизации
            this.checkAuthentication();
            
            // Перенаправляем на главную страницу
            await router.push({name: 'home'});
          }
        }
      } catch (error) {
        this.stopMessageRotation();
        // Показываем ошибку от сервера, если она есть
        const errorMessage = error.response?.data?.detail || 
                           error.response?.data?.message || 
                           error.response?.data?.error ||
                           error.message ||
                           'Ошибка при подключении аккаунта Ozon';
        this.error = errorMessage;
      }
    },
    /**
     * Автоматически перенаправляет неавторизованного пользователя на Ozon OAuth
     * Сценарий 2: Пользователь не авторизован
     */
    async redirectUnauthenticatedUser() {
      try {
        const oauthUrl = await this.getOzonOAuthUrl();
        if (oauthUrl) {
          // Сохраняем URL для немедленного перехода
          this.savedOAuthUrl = oauthUrl;
          
          // Запускаем счетчик обратного отсчета на 10 секунд
          this.countdown = 10;
          
          this.countdownInterval = setInterval(() => {
            this.countdown--;
            
            if (this.countdown <= 0) {
              clearInterval(this.countdownInterval);
              window.location.href = oauthUrl;
            }
          }, 1000);
        } else {
          this.error = 'Не удалось получить URL для авторизации Ozon';
        }
      } catch (error) {
        this.error = error.response?.data?.detail || 'Ошибка при получении URL авторизации Ozon';
      }
    },
    /**
     * Немедленно перенаправляет неавторизованного пользователя на Ozon OAuth
     * Используется при нажатии на кнопку "Перейти сейчас"
     */
    redirectUnauthenticatedUserImmediately() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      if (this.savedOAuthUrl) {
        window.location.href = this.savedOAuthUrl;
      } else {
        // Если URL еще не получен, получаем его и перенаправляем
        this.getOzonOAuthUrl().then(oauthUrl => {
          if (oauthUrl) {
            window.location.href = oauthUrl;
          }
        });
      }
    }
  },
  async mounted() {
    // Извлекаем параметры OAuth из URL
    this.extractOAuthParams();
    
    // Проверяем авторизацию
    this.checkAuthentication();
    
    if (this.isAuthenticated) {
      // Сценарий 1: Пользователь авторизован
      // Если есть OAuth параметры в URL - обрабатываем callback
      if (this.oauthCode) {
        await this.handleOAuthCallback();
      } else if (this.shouldAutoRedirect) {
        // Если указан параметр автоматического перенаправления - перенаправляем с задержкой
        await this.redirectToOzon(10000);
      }
    } else {
      // Сценарий 2: Пользователь не авторизован
      // Если есть OAuth параметры в URL - обрабатываем callback
      if (this.oauthCode) {
        await this.handleOAuthCallback();
      } else {
        // Автоматически перенаправляем на Ozon OAuth
        await this.redirectUnauthenticatedUser();
      }
    }
    
    // Проверяем, есть ли сообщение от регистрации
    if (this.$route.query.message) {
      this.successMessage = this.$route.query.message;
    }
  },
  beforeUnmount() {
    // Очищаем интервалы при размонтировании компонента
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.stopMessageRotation();
  }
}
</script>

<style scoped>
.success {
  color: #198754;
  background-color: #d1e7dd;
  border: 1px solid #badbcc;
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
}

.error {
  color: #dc3545;
  margin-bottom: 1rem;
}

.processing-message {
  min-height: 3rem;
  display: flex;
  align-items: center;
}

.fade-message {
  font-size: 1.1rem;
  margin: 0;
  font-weight: 500;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>